import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { clearSessionToken, getSessionToken } from "@/lib/auth";
import { getAdminToken } from "@/lib/device";

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkPendingRefunds = useCallback(async () => {
    // Fetch credits that were refunded because a previous analyze job crashed
    // (BAD GATEWAY / server restart) and inform the user via toast. Server
    // marks them 'notified' in the same call, so this runs once per event.
    try {
      const r = await api.get("/credits/refunds/pending");
      const total = Number(r?.data?.total_amount || 0);
      if (total > 0) {
        toast.success(
          <span data-testid="credit-refund-toast">
            {t("credits.refund_toast_title", { count: total })}
          </span>,
          {
            description: t("credits.refund_toast_body"),
            duration: 10000,
            className: "credit-refund-toast",
          },
        );
      }
    } catch {
      /* silent — refund check must never block auth */
    }
  }, [t]);

  const refresh = useCallback(async () => {
    // If we are on the WorkOS callback route (has ?code=), skip: AuthCallback
    // will exchange the code and re-trigger refresh via context.
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/auth/callback"
    ) {
      setLoading(false);
      return;
    }
    if (!getSessionToken() && !getAdminToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      // Fire-and-forget refund check right after we know the user is real.
      checkPendingRefunds();
    } catch {
      // Only clear the session token; leave the legacy admin token intact
      // so the URL bypass keeps working across reloads.
      if (getSessionToken()) clearSessionToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [checkPendingRefunds]);

  const logout = useCallback(async () => {
    let workosLogoutUrl = "";
    try {
      const r = await api.post("/auth/logout", {
        return_to: window.location.origin + "/",
      });
      workosLogoutUrl = r?.data?.workos_logout_url || "";
    } catch {
      /* ignore network errors on logout */
    }
    clearSessionToken();
    setUser(null);
    // Redirect through WorkOS logout URL so their upstream session cookie
    // is cleared too — otherwise the next "Sign in" silently re-auths as
    // the same user without showing the account picker.
    if (workosLogoutUrl) {
      window.location.href = workosLogoutUrl;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
